import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useBasePath } from '../../../lib/use-base-path';
import { queryPath } from '../../../lib/query-path';
import { useNarrow } from '../../../lib/use-narrow';
import { useConversations, getRecentConversationId } from '../../../lib/conversations';
import { useMorningBrief } from '../../../lib/brief';
import { ChatSurface } from '../ChatSurface';
import { useActiveConversation } from '../../chat/ActiveConversationContext';
import { useConversationRow } from '../../../lib/use-conversation-row';
import { useStartNewConversation } from '../../chat/use-start-new-conversation';
import { useChatOverlayOpen } from '../../../lib/use-chat-overlay';
import { useChatDockWidth } from '../../../lib/use-chat-dock-width';
import { ChatResizeHandle } from './ChatResizeHandle';
import { useChatFilePane, useRegisterChatFilePaneHost } from '../../../lib/chat-file-pane';
import { useChatFilePaneEnabled } from '../../../lib/chat-file-pane-flag';
import { dismissDocViewer, useDocViewer, type OpenDocViewer } from '../../../lib/doc-viewer-presenter';
import { DocViewerPane } from '../doc-viewer/DocViewerPane';
import { useMaestroWorking } from '../../../lib/maestro-working';
import { useTranslation } from '../../../lib/i18n';
import { useProductName } from '../../../lib/use-brand';
import { ChatFab } from '../ChatFab';
import { homeShowsAgentsItself, useHomeVariant } from '../../../lib/home-variant';
import { MobileChatLauncher } from '../MobileChatLauncher';
import { showsMobileChatLauncher } from '../../../lib/mobile-chat-launcher-routes';
import { useKeyboardInset, useKeyboardPanOffset } from '../use-keyboard-inset';
import { ChatHeader } from './ChatHeader';
import { Badge } from '../ui';
import { useLocale } from '../../../lib/locale-context';
import { useMeetingConversation } from '../../../lib/meeting-conversation-store';
import { meetingDuration, meetingTimeRange } from '../../../lib/meeting-time';
import { useVoiceCallSummary } from '../../../lib/voice-call-presence-context';
import { conversationLabel, dateTimeLabel } from './chat-mode';
import { useChatUrlSync } from './use-chat-url';
import { PACK_HANDOFF_PARAM } from '../../../lib/pack-handoff';
import { CHATS_DOCK_WIDTH, useChatsLayout } from '../../../lib/use-chats-layout';
import {
  CHAT_CLOSE_MS,
  EXPANDED_NARROW,
  EXPANDED_WIDE,
  EXPAND_TRANSITION,
  FULLSCREEN_WIDE,
} from './styles';

export { CHAT_DOCK_WIDTH } from './styles';

/**
 * The one chat. Mounted app-wide in AppFrame and ALWAYS present — its SIZE
 * changes, not its existence. On wide it's a right-docked column (`expanded`) that
 * pushes the content aside, or grown to `fullscreen`; on a phone every open size is
 * full-screen. The inner <ChatSurface> stays mounted across the resize AND across
 * route changes, so a live SSE stream and an in-progress voice call ride straight
 * through — there's only ever one useChat / one voice call.
 */
export function FloatingChat() {
  const narrowViewport = useNarrow();
  const chatsLayout = useChatsLayout();
  const narrow = narrowViewport || chatsLayout.stacked;
  // The Agents World home carries its own composer on wide screens.
  const homeVariant = useHomeVariant();
  const router = useRouter();
  const basePath = useBasePath();
  const { pathname } = router;
  const { t } = useTranslation();
  const { locale } = useLocale();
  const maestro = useMaestroWorking(false);
  const productName = useProductName();
  const { state: conversations } = useConversations(true);
  const { activeConversationId, setActiveConversationId, chatMode, setChatMode, openMaestroChat, retainChatExpanded, briefPeekId } = useActiveConversation();
  const startFreshConversation = useStartNewConversation();
  // A live call takes over the panel with its own header (VoiceCallOverlay's top bar: collapse +
  // settings, per Figma 12835:79206), so the normal chat header is hidden while it's up.
  const voiceCallActive = useVoiceCallSummary()?.timing.surfaceVisible ?? false;

  useChatUrlSync();

  const mode = chatMode;
  const list = conversations.kind === 'ready' ? conversations.conversations : [];
  // The list is one page of conversations, so the chat you are in may not be in it — a chat just created,
  // or one older than the page. Its row is what the header's title and its pin / rename / delete read.
  const active = useConversationRow(
    activeConversationId ?? undefined,
    activeConversationId ? list.find((c) => c.id === activeConversationId) : undefined,
  );
  const isNewChat = !activeConversationId;

  // The latest morning brief is labelled off the brief store, matching the native apps
  // (the list now carries brief rows too, but this title predates that and keys on the brief).
  const brief = useMorningBrief();
  // The brief is a transient peek, never the resumable thread. If it ever leaked into
  // the active/stored pointer (an older build persisted it, or a stale sessionStorage
  // entry), home would keep reopening the brief instead of the real last chat. When we
  // see the brief as the active thread WITHOUT an active peek, replace it with the real
  // last chat so resume lands where the user expects.
  useEffect(() => {
    const briefId = brief?.conversationId;
    if (!briefId || activeConversationId !== briefId || briefPeekId === briefId) return;
    let cancelled = false;
    void getRecentConversationId(24 * 60 * 60 * 1000).then((id) => {
      if (!cancelled) setActiveConversationId(id);
    });
    return () => {
      cancelled = true;
    };
  }, [brief, activeConversationId, briefPeekId, setActiveConversationId]);
  const isBriefOpen = !!brief && !!activeConversationId && activeConversationId === brief.conversationId;
  const briefStamp = isBriefOpen ? dateTimeLabel(brief.generatedDate) : undefined;
  const briefTitle = isBriefOpen ? `${t('chat.briefTitle')}${briefStamp ? ` · ${briefStamp}` : ''}` : undefined;
  // A meeting's write-up thread wears a "Meeting" pill and the meeting's own time under the title —
  // the same facts the card's header shows, from the same marker (`meetingNotesCard`).
  const meetingCard = useMeetingConversation(activeConversationId);
  const meetingSubtitle = useMemo(() => {
    if (!meetingCard) return undefined;
    const range = meetingTimeRange(meetingCard.startedAt, meetingCard.endedAt, Date.now(), locale, {
      today: t('agents.activity.today'),
      yesterday: t('agents.activity.yesterday'),
    });
    const duration = meetingDuration(meetingCard.startedAt, meetingCard.endedAt);
    const durationLabel = duration
      ? duration.hours > 0
        ? t('meetings.duration.hoursMinutes', { hours: duration.hours, minutes: duration.minutes })
        : t('meetings.duration.minutes', { count: duration.minutes })
      : '';
    return [range, durationLabel].filter(Boolean).join(' · ') || undefined;
  }, [meetingCard, locale, t]);
  // Hold the stable "New … chat" label while a just-created thread materializes so
  // the title only ever advances New chat → date → name, never flickering backward.
  const headerTitle = activeConversationId
    ? briefTitle ?? (active ? conversationLabel(active) : t('chat.newChatProduct', { productName }))
    : t('chat.newChatProduct', { productName });

  const open = useChatOverlayOpen();
  const dockWidth = useChatDockWidth();
  // The full-screen mobile chat is `position: fixed; inset: 0; height: 100dvh`. In-app
  // webviews (Instagram/LinkedIn) — like iOS Safari — don't shrink the layout viewport (or
  // 100dvh) when the on-screen keyboard opens, so the bottom-pinned composer sits BEHIND the
  // keyboard. visualViewport does report the gap, so shrink the panel by it to keep the
  // composer above the keyboard (the message ScrollArea is flex:1 and absorbs the change).
  // 0 on browsers that already resize the layout viewport, and irrelevant on wide (no OS keyboard).
  const keyboardInset = useKeyboardInset();
  // The narrow panel is `position: fixed`, pinned to the LAYOUT viewport. When iOS Safari
  // pans the page to keep a focused composer above the keyboard, the VISUAL (visible)
  // viewport scrolls down without moving the layout one — so the panel, and the header at
  // its top, scroll out of the visible window by that same amount. Counter-translate it back.
  const keyboardPanOffset = useKeyboardPanOffset();
  const [dragging, setDragging] = useState(false);
  // The per-chat Files/Media panel: opened from the header, rendered by ChatSurface
  // (which owns the messages it lists). Only offered once a real conversation exists.
  const [filesOpen, setFilesOpen] = useState(false);
  // Snapshot the fullscreen state so the close animation reads the right geometry
  // after chatMode has flipped to `bubble`.
  // `fullscreen` is a WIDE mode (on narrow the chat already covers the screen). The /chats screen used to be
  // excluded too, which left its header's ⤢ with nowhere to go — the reason QA filed "missing option for full
  // chat screen when the list chat is open". Its split column is a dock like any other, so growing it is the
  // same mode; AppFrame drops --chat-w to 0 for it, so the list sits full-width underneath rather than being
  // pushed by a dock that is no longer there.
  const liveFullscreen = !narrow && mode === 'fullscreen';
  const [closing, setClosing] = useState(false);
  const lastFullscreenRef = useRef(liveFullscreen);
  if (open) lastFullscreenRef.current = liveFullscreen;
  const prevOpenRef = useRef(open);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (open) {
      setClosing(false);
      return;
    }
    if (wasOpen) {
      setClosing(true);
      const timer = window.setTimeout(() => setClosing(false), CHAT_CLOSE_MS);
      return () => window.clearTimeout(timer);
    }
  }, [open]);

  const fullscreen = open ? liveFullscreen : lastFullscreenRef.current;

  // A file opened FROM THE CHAT docks beside the conversation instead of in a modal above it
  // (`lib/chat-file-pane.ts` decides when). The chat then takes the fullscreen geometry, the file pane fills
  // its inline-start side and the conversation keeps its dock width at inline-end.
  useRegisterChatFilePaneHost();
  const filePane = useChatFilePane();
  // A closed file stays DRAWN for one transition, so closing is an animation rather than a jump. Without it
  // the pane vanished the instant its request ended while the panel was still mid-resize: closing the file
  // left the conversation stretched across the whole shrinking panel, and closing the chat snapped the split
  // down to the dock in the middle of its slide-out. Same render-time ref pattern as `lastFullscreenRef`.
  //
  // Only a pane that is actually LEAVING is held: its request was dismissed, or the chat is closing with it.
  // A pane that merely stopped fitting (narrow, the stacked /chats layout) has its request re-presented as the
  // modal at once, and holding the old pane behind that modal would draw the file twice.
  const docRequest = useDocViewer();
  const lastPaneRef = useRef<OpenDocViewer | null>(null);
  const [, releasePane] = useState(0);
  if (filePane) lastPaneRef.current = filePane;
  else if (open && lastPaneRef.current && docRequest?.id === lastPaneRef.current.id) lastPaneRef.current = null;
  const leavingPane = filePane ? null : lastPaneRef.current;
  useEffect(() => {
    if (filePane || !lastPaneRef.current) return;
    const timer = window.setTimeout(() => {
      lastPaneRef.current = null;
      releasePane((n) => n + 1);
    }, CHAT_CLOSE_MS);
    return () => window.clearTimeout(timer);
  }, [filePane]);
  const shownPane = filePane ?? leavingPane;
  // The chat's GEOMETRY: still the split while the chat slides out with its file, so the two leave together.
  // A file closed in an open chat lets the panel shrink back to the dock at once, and the pane, still drawn,
  // folds away inside it beside the conversation's fixed width.
  const split = filePane != null || (!open && leavingPane != null);
  // The pane belongs to the chat: closing the chat closes it, rather than the same request re-presenting
  // itself as a modal over the page the reader just returned to.
  // Only under the experiment: closed, a chat-opened file is the ordinary modal, which outlives the chat as it always has.
  const filePaneEnabled = useChatFilePaneEnabled();
  // On the CLOSE only, not whenever the chat is closed: Discuss marks a file `besideChat` a moment before the
  // chat it navigates to has opened, and that request must wait for the chat rather than be dismissed.
  // The request the chat closed on is remembered rather than judged on the spot, because the experiment can
  // still be unresolved at that moment — and if it then resolves open, that file is the chat's and goes too.
  const wasOpenRef = useRef(open);
  const closedOnRef = useRef<number | null>(null);
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = open;
    if (open || !docRequest?.besideChat) closedOnRef.current = null;
    else if (wasOpen) closedOnRef.current = docRequest.id;
    if (filePaneEnabled && closedOnRef.current === docRequest?.id && docRequest) {
      closedOnRef.current = null;
      dismissDocViewer();
    }
  }, [filePaneEnabled, open, docRequest]);

  // Once the chat has been opened it stays MOUNTED for the rest of the session —
  // collapsing to the bubble only hides it. Unmounting used to tear down
  // <ChatSurface> and with it the single useChat: an in-flight turn's SSE was
  // aborted mid-thought, and a thread whose id hadn't come back from the server
  // yet was lost, so reopening dropped the user into a brand-new conversation.
  // Since a navigation can collapse the chat (narrow), that made "click a nav
  // item" silently kill the turn you were waiting on.
  const everOpenedRef = useRef(false);
  if (open) everOpenedRef.current = true;
  const resting = !open && !closing;
  // Nothing to preserve before the first open — stay unmounted so the chat costs
  // nothing (no conversation fetch, no duplexer) for a user who never opens it.
  const mounted = !resting || everOpenedRef.current;

  // Side dock only: a user-resized width (default CHAT_DOCK_WIDTH) that AppFrame's
  // --chat-w mirrors so the content pane reflows with it. Fullscreen/narrow ignore it.
  const docked = !narrow && !fullscreen && !split;
  const coversContent = fullscreen || split;
  const geometry = narrow ? EXPANDED_NARROW : coversContent ? FULLSCREEN_WIDE : { ...EXPANDED_WIDE, width: chatsLayout.split ? CHATS_DOCK_WIDTH : dockWidth };
  // Same div whether side dock or fullscreen — only the geometry/chrome differ, so
  // the transition animates the grow/shrink in place (no cross-fade, no remount).
  const panelStyle: CSSProperties = {
    ...geometry,
    // The personal-sale banner is a top band that must show ABOVE the chat, not be covered by this z-50
    // sheet. Two DIFFERENT bands, each 0px when absent:
    //  - `--personal-sale-banner-h`: the IN-CONTENT banner (mobile any screen, desktop non-/packages). It
    //    sits inside the content region, so only a sheet that covers that region clears it — NARROW (fixed
    //    inset:0) and wide FULLSCREEN. A desktop DOCK is beside the content, so the banner shows next to it.
    //  - `--personal-sale-topbar-h`: the desktop /packages full-viewport blue strip above <main>. It spans
    //    the whole top, so EVERY non-narrow sheet — dock AND fullscreen — must clear it or it covers the X.
    //
    // Narrow ALSO drops below the minimized-call strip (--voice-island-h, set by VoiceCallStrip) and off
    // the on-screen keyboard (keyboardInset); all subtract from the height so the sheet still reaches the
    // viewport bottom. `top` is in EXPAND_TRANSITION so the dock↔fullscreen shift eases rather than jumps.
    // `keyboardPanOffset` handles iOS Safari panning the PAGE (not just shrinking it) to keep the focused
    // composer above the keyboard: a `position: fixed` panel doesn't move with that pan, so without the
    // translate it (and the header at its top) scrolls out of the visible window. The pan also has to come
    // back out of `height` (on top of `keyboardInset`) or the translated panel overshoots the keyboard by
    // the same amount, hiding the composer behind it again.
    ...(narrow
      ? {
          top: 'calc(var(--personal-sale-banner-h, 0px) + var(--voice-island-h, 0px))',
          height: `calc(100dvh - var(--personal-sale-banner-h, 0px) - var(--voice-island-h, 0px) - ${keyboardInset}px - ${keyboardPanOffset}px)`,
          transform: keyboardPanOffset ? `translateY(${keyboardPanOffset}px)` : undefined,
        }
      : coversContent
        ? {
            top: 'calc(var(--personal-sale-banner-h, 0px) + var(--personal-sale-topbar-h, 0px))',
            height: 'calc(100dvh - var(--personal-sale-banner-h, 0px) - var(--personal-sale-topbar-h, 0px))',
          }
        : {
            // Desktop DOCK: only the /packages topbar strip needs clearing (--personal-sale-banner-h is 0
            // on /packages, and off /packages the in-content banner shows beside the dock, so this stays
            // at top:0 there — the var is absent → 0px, no change to the existing docked behaviour).
            top: 'var(--personal-sale-topbar-h, 0px)',
            height: 'calc(100dvh - var(--personal-sale-topbar-h, 0px))',
          }),
    // No eased width while dragging the resize handle, or the edge lags the cursor.
    transition: dragging ? 'none' : EXPAND_TRANSITION,
    ...(closing && !open
      ? {
          animation: narrow
            ? `orion-sheet-out ${CHAT_CLOSE_MS}ms cubic-bezier(0.32, 0.72, 0, 1) forwards`
            : `orion-chat-slide-out ${CHAT_CLOSE_MS}ms cubic-bezier(0.32, 0.72, 0, 1) forwards`,
          pointerEvents: 'none',
        }
      : null),
    // At rest: laid out (so the scroll position and message metrics survive) but
    // invisible, untabbable and out of the a11y tree — visibility:hidden does all
    // three. `animation: none` matters too: the panel is no longer remounted on
    // open, so clearing the name here is what lets the entrance animation replay
    // when the geometry's animation is set again.
    ...(resting ? { visibility: 'hidden', pointerEvents: 'none', animation: 'none' } : null),
  };

  return (
    <>
      {resting && !chatsLayout.onChats &&
        // Narrow: the FAB opens an input-only composer first, which hands off on send. It appears only
        // on the three screens the native apps float it on (`showsMobileChatLauncher`) — on a phone it
        // floats OVER the content, so anywhere else it is just something covering the last row. Wide is
        // NOT gated: there it sits beside the content and is the only door into the dock.
        (narrow ? (
          showsMobileChatLauncher(pathname) ? <MobileChatLauncher /> : null
        ) : homeShowsAgentsItself(homeVariant) && (pathname === '/' || pathname === '/agents') ? null : (
          // The suppression above is "this screen already has a composer of its own", NOT "this is a
          // new-home route" — and the two are not the same thing. The OVERVIEW home draws no
          // composer: its design puts a round launcher at the bottom-right instead (Figma
          // 19106:277823), so suppressing the FAB there left the desktop with no door into the chat
          // at all. Only the AGENTS WORLD home carries its own, so only it is suppressed — on `/`,
          // where it is the home, and on `/agents`, the other route it renders.
          //
          // Wide: resume the active thread if there is one, else start a fresh Maestro chat.
          <ChatFab
            onClick={() => (activeConversationId ? setChatMode('expanded') : openMaestroChat())}
            label={t('chat.openProduct', { productName })}
            // This branch only renders while the chat is CLOSED, which is exactly when the indicator
            // belongs: with the chat open the transcript is already saying it.
            working={maestro.working}
            workingLabel={t('agentsIsland.maestroWorking')}
          />
        ))}
      {mounted && (
        <div style={{ ...panelStyle, flexDirection: 'row' }}>
          {shownPane && (
            <DocViewerPane
              key={shownPane.id}
              file={shownPane.file}
              kind={shownPane.kind}
              pageName={shownPane.pageName}
              fileHref={shownPane.fileHref}
              workspaceName={shownPane.workspaceName}
              inFileTree={shownPane.inFileTree}
              startEditing={shownPane.editing}
              directory={shownPane.directory}
              draft={shownPane.draft}
              vfsPath={shownPane.vfsPath}
              requestId={shownPane.id}
              onClose={dismissDocViewer}
            />
          )}
          {/* The conversation column. ALWAYS this one element, split or not, so opening or closing a file
              never remounts <ChatSurface> — the live stream and a voice call ride straight through, exactly
              as they do across the dock↔fullscreen resize. Beside a file it keeps the dock's width (and
              its resize handle, now on the boundary between the two). */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              minWidth: 0,
              ...(shownPane ? { flex: '0 0 auto', width: dockWidth } : { flex: '1 1 auto' }),
            }}
          >
            {/* The plain dock has no handle on the /chats split (its width is the list's, set by the page's own
                handle). Beside a file it does: the boundary is then between the file and the conversation, and
                the page's handle is under the split chat, out of reach. */}
            {open && (split || (docked && !chatsLayout.split)) && (
              <ChatResizeHandle width={dockWidth} label={t('chat.resizeHandle')} onDraggingChange={setDragging} />
            )}
            {!voiceCallActive && (
            <ChatHeader
              narrow={narrow}
              // Beside a file the chat is a column, not fullscreen, whatever `chatMode` says — so its header offers
              // ⤢, which closes the file and grows the conversation (see `onExpand`).
              fullscreen={fullscreen && !split}
              isNewChat={isNewChat}
              headerTitle={headerTitle}
              badge={meetingCard ? <Badge variant="agents" label={t('meetings.notes.badge')} /> : undefined}
              subtitle={meetingSubtitle}
              conversation={active}
              chatsScreen={chatsLayout.onChats}
              onBackToChats={() => {
                setChatMode('bubble');
                const query = { ...router.query };
                delete query.conversation;
                delete query.message;
                delete query.new;
                delete query.chat;
                // A thread list is not a pack arrival. See `PACK_HANDOFF_PARAM`.
                delete query[PACK_HANDOFF_PARAM];
                void router.push({ pathname: '/chats', query }, queryPath(`${basePath}/chats`, query), { shallow: true });
              }}
              onNewConversation={() => {
                // Keep the open chat expanded across the switch — clearing the thread makes
                // it an empty landing, which on the home route would otherwise self-collapse
                // to the bubble (MessagesPanel's "no second home" rule).
                retainChatExpanded();
                void startFreshConversation();
              }}
              onCompose={() => chatsLayout.onChats ? void startFreshConversation() : openMaestroChat()}
              onExpand={() => {
                if (split) dismissDocViewer();
                setChatMode('fullscreen');
              }}
              onShrink={() => setChatMode('expanded')}
              onMinimize={() => setChatMode('bubble')}
              onOpenFiles={isNewChat ? undefined : () => setFilesOpen(true)}
            />
            )}
            {/* The chat surface fills the rest; minHeight:0 lets the inner scroll area
                shrink instead of overflowing the panel. */}
            <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
              <ChatSurface
                visible={open && !filesOpen}
                variant="floating"
                expanded
                sideDocked={!narrow && (!fullscreen || split)}
                filesOpen={filesOpen && !isNewChat}
                onCloseFiles={() => setFilesOpen(false)}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
